import { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Send, Compass, Mail, PenLine, LayoutDashboard, Megaphone, Newspaper, Podcast, LucideIcon } from "lucide-react";
import { track } from "./navConfig";
import { Micro, GhostWord, FOCUS, REGION, delay } from "./primitives";

const XIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const EcoRow = ({ to, icon: Icon, label, desc, external, accent, testid }: {
  to: string; icon: LucideIcon; label: string; desc: string; external?: boolean; accent?: boolean; testid: string;
}) => {
  const cls = `group/eco flex items-start gap-3 rounded-lg px-2.5 py-2 -mx-2.5 hover:bg-white/[0.04] transition-colors ${FOCUS}`;
  const inner = (
    <>
      <span className={`mt-0.5 h-8 w-8 shrink-0 rounded-md border flex items-center justify-center transition-colors ${
        accent
          ? "border-[#7C3AED]/35 bg-[#7C3AED]/[0.12] text-[#A78BFA] group-hover/eco:text-[#00E5FF] group-hover/eco:border-[#00E5FF]/35"
          : "border-white/10 bg-white/[0.03] text-gray-500 group-hover/eco:text-[#00E5FF] group-hover/eco:border-[#00E5FF]/30"}`}>
        <Icon className="h-4 w-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-gray-200 group-hover/eco:text-white transition-colors">{label}</span>
        <span className="block text-xs text-gray-500 leading-snug">{desc}</span>
      </span>
      <ArrowUpRight aria-hidden className="h-3.5 w-3.5 shrink-0 mt-1.5 text-[#00E5FF] opacity-0 -translate-x-1
        group-hover/eco:opacity-100 group-hover/eco:translate-x-0 motion-safe:transition-all motion-safe:duration-200" />
    </>
  );
  return external ? (
    <a href={to} target={to.startsWith("http") ? "_blank" : undefined} rel="noreferrer" data-testid={testid}
      onClick={() => track("nav_click", { to })} className={cls}>{inner}</a>
  ) : (
    <Link to={to} data-testid={testid} onClick={() => track("nav_click", { to })} className={cls}>{inner}</Link>
  );
};

const Module = ({ title, i, children, foot }: { title: string; i: number; children: ReactNode; foot?: ReactNode }) => (
  <section className={`flex flex-col rounded-xl border border-white/[0.07] bg-white/[0.015] p-4 ${REGION}`} style={delay(i)}>
    <Micro className="mb-3 px-0.5">{title}</Micro>
    <div className="space-y-0.5 flex-1">{children}</div>
    {foot}
  </section>
);

const ConnectChip = ({ to, icon: Icon, label, external, testid }: {
  to: string; icon: any; label: string; external?: boolean; testid: string;
}) => {
  const cls = `group/chip inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.02] px-3.5 py-1.5
    text-xs font-semibold text-gray-400 hover:text-white hover:border-[#00E5FF]/30 hover:-translate-y-px
    motion-safe:transition-[color,border-color,transform] motion-safe:duration-200 ${FOCUS}`;
  const inner = (
    <>
      <Icon className="h-3.5 w-3.5 text-gray-500 group-hover/chip:text-[#00E5FF] transition-colors" />
      {label}
    </>
  );
  return external ? (
    <a href={to} target="_blank" rel="noreferrer" data-testid={testid} onClick={() => track("nav_click", { to })} className={cls}>{inner}</a>
  ) : (
    <Link to={to} data-testid={testid} onClick={() => track("nav_click", { to })} className={cls}>{inner}</Link>
  );
};

export const MoreMenu = () => (
  <div data-testid="layernav-more" className="relative p-6">
    <GhostWord word="More" />
    <div className="relative grid grid-cols-1 md:grid-cols-3 gap-4">
      <Module title="Discover NewsLayer" i={0}
        foot={
          <div className="mt-3 pt-3 border-t border-white/[0.06] flex items-center gap-1.5 px-0.5">
            <Link to="/privacy" data-testid="more-link-privacy-policy" onClick={() => track("nav_click", { to: "/privacy" })}
              className={`text-[11px] text-gray-600 hover:text-gray-300 transition-colors rounded ${FOCUS}`}>
              Privacy Policy
            </Link>
            <span aria-hidden className="text-gray-700 text-[11px]">·</span>
            <Link to="/terms" data-testid="more-link-terms-of-service" onClick={() => track("nav_click", { to: "/terms" })}
              className={`text-[11px] text-gray-600 hover:text-gray-300 transition-colors rounded ${FOCUS}`}>
              Terms of Service
            </Link>
          </div>
        }>
        <EcoRow to="/about" icon={Compass} label="About NewsLayer" desc="The mission behind the layer" testid="more-link-about-newslayer" />
        <EcoRow to="mailto:newslayerapp@gmail.com" external icon={Mail} label="Contact" desc="Reach the NewsLayer desk" testid="more-link-contact" />
      </Module>

      <Module title="Build With Us" i={1}>
        <EcoRow to="/author-program" icon={PenLine} label="Become an Author" desc="Join the approved author program" testid="more-link-become-an-author" />
        <EcoRow to="/studio" icon={LayoutDashboard} label="Author Studio" desc="Manage submissions & publishing" testid="more-link-author-studio" />
      </Module>

      <Module title="For Business" i={2}>
        <EcoRow to="/advertise" accent icon={Megaphone} label="Advertise" desc="Reach the onchain economy" testid="more-link-advertise" />
        <EcoRow to="/pr" accent icon={Newspaper} label="PR Publication" desc="Publish company announcements" testid="more-link-pr-publication" />
      </Module>
    </div>

    <div className={`relative mt-4 flex flex-wrap items-center gap-2.5 border-t border-white/[0.06] pt-4 ${REGION}`}
      style={delay(3)} data-testid="more-connect-rail">
      <Micro cyan className="mr-2">Connect</Micro>
      <ConnectChip to="https://x.com/newslayerapp" external icon={XIcon} label="X" testid="more-link-x-twitter" />
      <ConnectChip to="https://t.me/newslayerapp" external icon={Send} label="Telegram" testid="more-link-telegram" />
      <ConnectChip to="/briefs" icon={Podcast} label="Podcast" testid="more-link-podcast" />
    </div>

    <Link to="/pr" data-testid="more-commercial-cta" onClick={() => track("nav_click", { more_cta: "pr" })}
      className={`group/cta relative mt-4 flex overflow-hidden rounded-xl border border-[#7C3AED]/30 bg-[#0b0a14]
        p-5 hover:border-[#7C3AED]/55 transition-colors duration-200 ${REGION} ${FOCUS}`} style={delay(4)}>
      <span aria-hidden className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-[#7C3AED] via-[#6366F1] to-[#00E5FF] opacity-70" />
      <span aria-hidden className="absolute -top-24 -left-14 h-56 w-56 rounded-full bg-[#7C3AED]/[0.13] blur-3xl
        group-hover/cta:bg-[#7C3AED]/[0.2] motion-safe:transition-colors motion-safe:duration-300" />
      <span aria-hidden className="absolute inset-0 opacity-40"
        style={{ backgroundImage: "radial-gradient(rgba(255,255,255,0.05) 1px, transparent 1px)", backgroundSize: "18px 18px" }} />
      <span className="relative flex flex-col sm:flex-row sm:items-center gap-4 w-full">
        <span className="h-11 w-11 shrink-0 rounded-lg border border-[#7C3AED]/40 bg-[#7C3AED]/[0.14] flex items-center justify-center">
          <Send className="h-5 w-5 text-[#A78BFA] group-hover/cta:text-[#00E5FF] transition-colors duration-200" />
        </span>
        <span className="min-w-0 flex-1">
          <Micro className="mb-1">Publish on NewsLayer</Micro>
          <span className="block font-heading font-bold text-white text-base leading-snug">
            Put your announcement in front of the onchain economy.
          </span>
          <span className="block text-xs text-gray-500 mt-1">Reviewed within one business day.</span>
        </span>
        <span data-testid="more-submit-release"
          className="inline-flex items-center gap-2 whitespace-nowrap self-start sm:self-center rounded-md
            bg-[#7C3AED] group-hover/cta:bg-[#8B5CF6] text-white font-bold text-sm px-4 py-2.5
            shadow-[inset_0_0_0_1px_rgba(0,229,255,0.22)]
            group-hover/cta:shadow-[inset_0_0_0_1px_rgba(0,229,255,0.4),0_0_18px_-4px_rgba(124,58,237,0.55)]
            motion-safe:transition-[background-color,box-shadow] motion-safe:duration-200">
          Submit a Release
          <ArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover/cta:translate-x-0.5" />
        </span>
      </span>
    </Link>
  </div>
);
